import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Share2, Check, X, Clock, Users, FolderOpen, Folder } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/auth";
import { toast } from "sonner";

interface SharedProject {
  id: string;
  project_id: string;
  owner_id: string;
  shared_with_email: string;
  accepted: boolean;
  created_at: string;
  permission_level: string;
  project: {
    name: string;
    description?: string;
  };
}

interface SharedFolder {
  id: string;
  folder_id: string;
  owner_id: string;
  shared_with_email: string;
  accepted: boolean;
  created_at: string;
  folder: {
    name: string;
    description?: string;
  };
}

interface SharedWithYouSectionProps {
  onProjectSelect?: (projectId: string) => void;
  onFolderSelect?: (folderId: string) => void;
}

const SharedWithYouSection: React.FC<SharedWithYouSectionProps> = ({ 
  onProjectSelect, 
  onFolderSelect 
}) => {
  const [sharedProjects, setSharedProjects] = useState<SharedProject[]>([]);
  const [sharedFolders, setSharedFolders] = useState<SharedFolder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchSharedItems();
    }
  }, [user]);

  const fetchSharedItems = async () => {
    if (!user?.email) return;

    try {
      setIsLoading(true);

      // Fetch shared projects
      const { data: projects, error: projectsError } = await supabase
        .from("project_shares")
        .select(`
          id,
          project_id,
          owner_id,
          shared_with_email,
          accepted,
          created_at,
          permission_level,
          project:projects!project_shares_project_id_fkey (
            name,
            description
          )
        `)
        .eq("shared_with_email", user.email)
        .eq("accepted", false)
        .order("created_at", { ascending: false });

      if (projectsError) throw projectsError;

      // Fetch shared folders
      const { data: folders, error: foldersError } = await supabase
        .from("folder_shares")
        .select(`
          id,
          folder_id,
          owner_id,
          shared_with_email,
          accepted,
          created_at,
          folder:folders!folder_shares_folder_id_fkey (
            name,
            description
          )
        `)
        .eq("shared_with_email", user.email)
        .eq("accepted", false)
        .order("created_at", { ascending: false });

      if (foldersError) throw foldersError;

      setSharedProjects(projects || []);
      setSharedFolders(folders || []);
    } catch (err: any) {
      console.error("Error fetching shared items:", err);
      toast.error("Kunde inte ladda delade objekt");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptProjectShare = async (shareId: string, projectId: string) => {
    try {
      const { error: updateError } = await supabase
        .from("project_shares")
        .update({ 
          accepted: true,
          shared_with_id: user?.id 
        })
        .eq("id", shareId);

      if (updateError) throw updateError;

      toast.success("Projektdelning accepterad!");
      fetchSharedItems();
      
      if (onProjectSelect) {
        onProjectSelect(projectId);
      }
    } catch (error: any) {
      console.error("Error accepting project share:", error);
      toast.error("Kunde inte acceptera projektdelning");
    }
  };

  const handleAcceptFolderShare = async (shareId: string, folderId: string) => {
    try {
      const { error: updateError } = await supabase
        .from("folder_shares")
        .update({ 
          accepted: true,
          shared_with_id: user?.id 
        })
        .eq("id", shareId);

      if (updateError) throw updateError;

      toast.success("Mappdelning accepterad!");
      fetchSharedItems();
      
      if (onFolderSelect) {
        onFolderSelect(folderId);
      }
    } catch (error: any) {
      console.error("Error accepting folder share:", error);
      toast.error("Kunde inte acceptera mappdelning");
    }
  };

  const handleRejectProjectShare = async (shareId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from("project_shares")
        .delete()
        .eq("id", shareId);

      if (deleteError) throw deleteError;

      toast.success("Projektdelning avvisad");
      fetchSharedItems();
    } catch (error: any) {
      console.error("Error rejecting project share:", error);
      toast.error("Kunde inte avvisa projektdelning");
    }
  };

  const handleRejectFolderShare = async (shareId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from("folder_shares")
        .delete()
        .eq("id", shareId);

      if (deleteError) throw deleteError;

      toast.success("Mappdelning avvisad");
      fetchSharedItems();
    } catch (error: any) {
      console.error("Error rejecting folder share:", error);
      toast.error("Kunde inte avvisa mappdelning");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-slate-800 flex items-center">
          <Share2 className="h-5 w-5 mr-2 text-primary" />
          Delade med dig
        </h3>
        <div className="text-center py-6">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-slate-500 mt-2">Laddar delade objekt...</p>
        </div>
      </div>
    );
  }

  const totalSharedItems = sharedProjects.length + sharedFolders.length;

  if (totalSharedItems === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-full mb-4 inline-block">
          <Share2 className="h-12 w-12 text-blue-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-800 mb-2">
          Du har {totalSharedItems} {totalSharedItems === 1 ? 'delning' : 'delningar'} att hantera
        </h3>
        <p className="text-slate-600">
          Kollegor har delat projekt och mappar med dig. Acceptera för att få tillgång.
        </p>
      </div>

      <div className="grid gap-4 max-w-2xl mx-auto">
        {/* Shared Projects */}
        {sharedProjects.map((share) => (
          <Card key={`project-${share.id}`} className="border-blue-200 hover:border-blue-300 transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-blue-600" />
                Projekt: {share.project?.name || "Namnlöst projekt"}
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  <Clock className="h-3 w-3 mr-1" />
                  Väntar på svar
                </Badge>
              </CardTitle>
              {share.project?.description && (
                <p className="text-sm text-slate-600">
                  {share.project.description}
                </p>
              )}
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="text-sm text-slate-600">
                  <p className="font-medium mb-1">Detta ger dig tillgång till:</p>
                  <ul className="text-xs space-y-1 ml-4 list-disc">
                    <li>Hela projektet med alla mappar och undermappar</li>
                    <li>Alla riskbedömningar och sammanställningar</li>
                    <li>{share.permission_level === 'read' ? 'Läsbehörighet' : 'Skrivrättigheter'}</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRejectProjectShare(share.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Avvisa
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAcceptProjectShare(share.id, share.project_id)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Acceptera projekt
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Shared Folders */}
        {sharedFolders.map((share) => (
          <Card key={`folder-${share.id}`} className="border-green-200 hover:border-green-300 transition-colors">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Folder className="h-5 w-5 text-green-600" />
                Mapp: {share.folder?.name || "Namnlös mapp"}
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  <Clock className="h-3 w-3 mr-1" />
                  Väntar på svar
                </Badge>
              </CardTitle>
              {share.folder?.description && (
                <p className="text-sm text-slate-600">
                  {share.folder.description}
                </p>
              )}
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="text-sm text-slate-600">
                  <p className="font-medium mb-1">Detta ger dig tillgång till:</p>
                  <ul className="text-xs space-y-1 ml-4 list-disc">
                    <li>Denna specifika mapp med innehåll</li>
                    <li>Alla riskbedömningar i mappen</li>
                    <li>Läs- och skrivrättigheter</li>
                  </ul>
                </div>
                
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRejectFolderShare(share.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Avvisa
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAcceptFolderShare(share.id, share.folder_id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Acceptera mapp
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SharedWithYouSection;